import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useHasMenu } from '../../../../auth/access';
import { ChartFrame } from '../../../dashboards/components/ChartCard';
import { AttendanceDay, hhmm, localDate, minutesLabel, myApi, statusStyle } from '../../../payroll/payroll.api';
import useAsyncData from '../../hooks/useAsyncData';

const SHOW = 7;
const DAY = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const EMPTY: AttendanceDay[] = [];
/** Days the employee was not expected at work, or that have not happened. */
const SKIP = new Set(['UPCOMING', 'NOT_EMPLOYED']);

const dayLabel = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return DAY.format(new Date(y, m - 1, d));
};

/** The last seven days up to today, newest first; early in a month it reaches into the last one. */
async function recentDays(): Promise<AttendanceDay[]> {
  const now = new Date();
  const today = localDate(now);
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const months = [myApi.attendance(year, month)];
  if (now.getDate() < SHOW) {
    const prev = new Date(year, month - 2, 1);
    months.unshift(myApi.attendance(prev.getFullYear(), prev.getMonth() + 1));
  }
  const reports = await Promise.all(months);
  return reports
    .flatMap((r) => r.days)
    .filter((d) => d.date.slice(0, 10) <= today && !SKIP.has(d.status))
    .slice(-SHOW)
    .reverse();
}

/**
 * Home's attendance log: the signed-in employee's last seven days — status, punch in and out,
 * hours and late marks. Needs My Attendance; the full month and punching live there.
 */
export default function AttendanceWidget() {
  const allowed = useHasMenu('my.attendance');
  if (!allowed) return null;
  return <AttendanceLog />;
}

function AttendanceLog() {
  const fetcher = useCallback(() => recentDays(), []);
  const { data, loading, error, refetch } = useAsyncData<AttendanceDay[]>(fetcher, EMPTY, {
    code: 'MY_ATTENDANCE_LOG_FAILED',
    errorMessage: 'Could not load your attendance',
    toastOnError: false,
  });

  const action = (
    <Button component={RouterLink} to='/my-attendance' size='small'>
      Punch in / out
    </Button>
  );

  return (
    <ChartFrame title='Attendance log · last 7 days' action={action}>
      {error ? (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      ) : loading ? (
        <Stack gap={1}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant='text' height={32} />
          ))}
        </Stack>
      ) : data.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No attendance recorded yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='center'>In</TableCell>
                <TableCell align='center'>Out</TableCell>
                <TableCell align='right'>Worked</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((d) => {
                const s = statusStyle(d.status);
                const note = d.holidayName ?? d.leaveType;
                return (
                  <TableRow key={d.date}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayLabel(d.date.slice(0, 10))}</TableCell>
                    <TableCell>
                      <Stack direction='row' gap={0.75} alignItems='center' flexWrap='wrap'>
                        <Box
                          component='span'
                          sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: s.bg, color: s.fg, fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                        >
                          {s.label}
                        </Box>
                        {d.isLate && (
                          <Box component='span' sx={{ color: 'warning.dark', fontSize: '0.75rem', fontWeight: 600 }}>
                            Late
                          </Box>
                        )}
                        {note && (
                          <Typography variant='caption' color='text.secondary'>
                            {note}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align='center'>{hhmm(d.punchIn) || '—'}</TableCell>
                    <TableCell align='center'>{hhmm(d.punchOut) || '—'}</TableCell>
                    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                      {minutesLabel(d.workedMinutes) || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </ChartFrame>
  );
}
